import { BUSINESS_UNIT_TYPES, type BusinessUnitType, type Station } from "@station/contracts";
import { diffDays, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  inputClassName,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Tone,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Compass } from "lucide-react";
import { type ReactElement, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { StationAssetsTab } from "../../../../features/station-assets.js";
import { StationForm } from "../../../../features/station-form.js";
import { STATION_STATUS_TONE } from "../../../../features/station-status.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";
import { useDocumentTitle, useOptimisticFlags } from "../../../../lib/hooks.js";
import { queryKeys } from "../../../../lib/query.js";
import { useMe, usePermissions } from "../../../../lib/session.js";
import { useTenantShape } from "../../../../lib/tenant-shape.js";

export const Route = createFileRoute("/_app/ayarlar/istasyonlar/$stationId")({
  component: StationDetailPage,
});

function useInvalidateStation(stationId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.stations() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.station(stationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.me }),
    ]);
  };
}

function StationDetailPage() {
  const { t } = useTranslation();
  const { stationId } = Route.useParams();
  const shape = useTenantShape();
  const station = useQuery({
    queryKey: queryKeys.station(stationId),
    queryFn: async () => (await api.GET("/stations/{id}", { params: { path: { id: stationId } } })).data!,
  });
  useDocumentTitle(station.data?.name ?? t("menu.stations"));
  const listLabel = shape.isSingleStation ? t("menu.station") : t("menu.stations");

  if (station.error) {
    return isApiError(station.error, "NOT_FOUND") ? (
      <EmptyState
        icon={<Compass />}
        title={t("stations.notFound.title")}
        description={t("stations.notFound.description")}
        action={
          <Button asChild>
            <Link to="/ayarlar/istasyonlar">{listLabel}</Link>
          </Button>
        }
      />
    ) : (
      <Alert tone="danger">{errorMessage(station.error)}</Alert>
    );
  }
  if (!station.data) return <SkeletonRows rows={6} />;
  const data = station.data;

  return (
    <>
      <PageHeader
        title={data.name}
        description={[data.code, data.companyName, data.regionName].filter(Boolean).join(" · ")}
        breadcrumb={[
          { label: t("menu.groups.settings") },
          {
            label: listLabel,
            link: (children, className) => (
              <Link to="/ayarlar/istasyonlar" className={className}>
                {children}
              </Link>
            ),
          },
          { label: data.name },
        ]}
        actions={
          <StatusBadge tone={STATION_STATUS_TONE[data.status]}>
            {t(`enums.stationStatus.${data.status}`)}
          </StatusBadge>
        }
      />
      <Tabs defaultValue="general">
        <TabsList aria-label={t("stations.tabs.label")}>
          <TabsTrigger value="general">{t("stations.tabs.general")}</TabsTrigger>
          <TabsTrigger value="businessUnits">{t("stations.tabs.businessUnits")}</TabsTrigger>
          <TabsTrigger value="assets">{t("stations.tabs.assets")}</TabsTrigger>
          <TabsTrigger value="licenses">{t("stations.tabs.licenses")}</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <GeneralTab station={data} />
        </TabsContent>
        <TabsContent value="businessUnits">
          <BusinessUnitsTab station={data} />
        </TabsContent>
        <TabsContent value="assets">
          <StationAssetsTab station={data} />
        </TabsContent>
        <TabsContent value="licenses">
          <LicensesTab station={data} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function ReadOnlyDetails({ station }: { station: Station }): ReactElement {
  const { t } = useTranslation();
  const rows: Array<[string, string | null]> = [
    [t("stations.fields.code"), station.code],
    [t("stations.fields.name"), station.name],
    [t("stations.fields.company"), station.companyName],
    [t("stations.fields.region"), station.regionName],
    [t("stations.fields.distributor"), station.distributorName],
    [t("common.phone"), station.phone],
    [t("common.city"), [station.district, station.city].filter(Boolean).join(" / ") || null],
    [t("common.address"), station.address],
    [t("stations.fields.activeFrom"), formatDate(station.activeFrom)],
  ];
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="text-sm font-medium">{value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function GeneralTab({ station }: { station: Station }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const invalidate = useInvalidateStation(station.id);
  const canManage = usePermissions().has("stations.manage") && station.status !== "TRANSFERRED_OUT";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const nextStatus = station.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

  const changeStatus = useMutation({
    mutationFn: async () =>
      api.PATCH("/stations/{id}", {
        params: { path: { id: station.id } },
        body: { status: nextStatus, version: station.version },
      }),
    onSuccess: async () => {
      await invalidate();
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  return (
    <div className="grid gap-4">
      {station.status === "TRANSFERRED_OUT" ? (
        <Alert tone="warning" title={t("stations.transferredOut.title")}>
          {t("stations.transferredOut.description", { date: formatDate(station.activeTo) })}
        </Alert>
      ) : null}
      <Card className="p-5 sm:p-6">
        {canManage ? (
          <StationForm
            key={station.version}
            station={station}
            onSaved={() => toast({ tone: "success", title: t("common.saved") })}
          />
        ) : (
          <ReadOnlyDetails station={station} />
        )}
      </Card>
      {canManage ? (
        <Card className="grid gap-2 p-5 sm:p-6">
          <h2 className="text-sm font-semibold">
            {station.status === "ACTIVE" ? t("stations.deactivate.title") : t("stations.activate.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {station.status === "ACTIVE"
              ? t("stations.deactivate.description")
              : t("stations.activate.description")}
          </p>
          <Button
            variant={station.status === "ACTIVE" ? "destructive" : "outline"}
            className="justify-self-start"
            onClick={() => setConfirmOpen(true)}
          >
            {station.status === "ACTIVE" ? t("common.deactivate") : t("common.activate")}
          </Button>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title={
              station.status === "ACTIVE" ? t("stations.deactivate.title") : t("stations.activate.title")
            }
            description={
              station.status === "ACTIVE"
                ? t("stations.deactivate.confirm", { code: station.code })
                : t("stations.activate.description")
            }
            tone={station.status === "ACTIVE" ? "danger" : "default"}
            // Kapatma kritik bir işlemdir: kullanıcıya istasyon kodu yazdırılır.
            {...(station.status === "ACTIVE" ? { confirmationText: station.code } : {})}
            confirmLabel={station.status === "ACTIVE" ? t("common.deactivate") : t("common.activate")}
            onConfirm={() => changeStatus.mutateAsync().then(() => undefined)}
          />
        </Card>
      ) : null}
    </div>
  );
}

function BusinessUnitsTab({ station }: { station: Station }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const invalidate = useInvalidateStation(station.id);
  const features = new Set<string>(useMe().data?.activeTenant?.features ?? []);
  const canManage = usePermissions().has("stations.manage") && station.status !== "TRANSFERRED_OUT";
  const optimistic = useOptimisticFlags();

  // `areaM2` GÖNDERİLMEZSE korunur; null gönderilirse temizlenir (gider dağıtımı m² ağırlığını buradan okur).
  const toggle = useMutation({
    mutationFn: async (input: { unitType: BusinessUnitType; isActive: boolean; areaM2?: string | null }) =>
      api.PUT("/stations/{id}/business-units", {
        params: { path: { id: station.id } },
        body: { units: [input] },
      }),
    onSuccess: invalidate,
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
    onSettled: (_data, _error, input) => optimistic.clear(input.unitType),
  });

  return (
    <Card className="p-5 sm:p-6">
      <p className="mb-4 text-sm text-muted-foreground">{t("stations.businessUnitsHint")}</p>
      <ul className="grid gap-2">
        {BUSINESS_UNIT_TYPES.map((unitType) => {
          const unit = station.businessUnits.find((item) => item.unitType === unitType);
          const inPlan = features.has(unitType);
          return (
            <li
              key={unitType}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3"
            >
              <label
                className={cn(
                  "flex items-center gap-3 text-sm font-medium",
                  !inPlan && "text-muted-foreground",
                )}
              >
                <Checkbox
                  checked={optimistic.value(unitType, unit?.isActive ?? false)}
                  disabled={!canManage || !inPlan || toggle.isPending}
                  onChange={(event) => {
                    optimistic.set(unitType, event.target.checked);
                    toggle.mutate({ unitType, isActive: event.target.checked });
                  }}
                />
                {t(`enums.businessUnit.${unitType}`)}
              </label>
              <span className="flex flex-wrap items-center gap-3">
                {unit?.isActive ? (
                  <AreaField
                    unitType={unitType}
                    areaM2={unit.areaM2}
                    disabled={!canManage || toggle.isPending}
                    onSave={(areaM2) => toggle.mutate({ unitType, isActive: true, areaM2 })}
                  />
                ) : null}
                <span className="text-xs text-muted-foreground">
                  {!inPlan
                    ? t("stations.unitNotInPlan")
                    : unit?.isActive
                      ? t("stations.unitActiveSince", { date: formatDate(unit.startedOn) })
                      : t("common.inactive")}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * İş biriminin alanı (m²). Gideri ALANA GÖRE dağıtan anahtarlar ağırlığı buradan okur; boş bırakılırsa
 * o iş birimi alan tabanlı dağıtımda pay almaz.
 */
function AreaField({
  unitType,
  areaM2,
  disabled,
  onSave,
}: {
  unitType: BusinessUnitType;
  areaM2: string | null;
  disabled: boolean;
  onSave: (areaM2: string | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState(areaM2 ?? "");
  useEffect(() => setText(areaM2 ?? ""), [areaM2]);
  return (
    <span className="flex items-center gap-1.5">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {t("stations.areaM2")}
      </label>
      <input
        id={id}
        inputMode="decimal"
        className={cn(inputClassName, "h-8 w-24 text-sm")}
        value={text}
        disabled={disabled}
        aria-label={t("stations.areaM2Label", { unit: t(`enums.businessUnit.${unitType}`) })}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          const next = text.trim();
          if (next === (areaM2 ?? "")) return;
          onSave(next === "" ? null : next);
        }}
      />
    </span>
  );
}

/** EPDK lisansının durumu: süresi dolmuş / 60 gün içinde dolacak / geçerli. Eşik yalnızca GÖRSEL bir uyarıdır. */
function licenseState(expiresOn: string | null): {
  tone: Tone;
  key: "missing" | "expired" | "expiring" | "valid";
  days: number;
} {
  if (!expiresOn) return { tone: "neutral", key: "missing", days: 0 };
  const days = diffDays(toIstanbulDate(new Date()), expiresOn as Parameters<typeof diffDays>[1]);
  if (days < 0) return { tone: "danger", key: "expired", days: -days };
  return days <= 60 ? { tone: "warning", key: "expiring", days } : { tone: "success", key: "valid", days };
}

function LicensesTab({ station }: { station: Station }): ReactElement {
  const { t } = useTranslation();
  const state = licenseState(station.epdkLicenseExpiresOn);
  return (
    <div className="grid gap-4">
      <Card className="p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">{t("stations.licenses.epdk")}</h2>
          <StatusBadge tone={state.tone}>
            {t(`stations.licenses.state.${state.key}`, { count: state.days })}
          </StatusBadge>
        </div>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">{t("stations.fields.epdkLicenseNo")}</dt>
            <dd className="font-mono text-sm font-medium">{station.epdkLicenseNo ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("stations.fields.epdkLicenseExpiresOn")}</dt>
            <dd className="text-sm font-medium tabular">{formatDate(station.epdkLicenseExpiresOn)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("stations.fields.distributor")}</dt>
            <dd className="text-sm font-medium">{station.distributorName ?? "—"}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-muted-foreground">{t("stations.licenses.editHint")}</p>
      </Card>
      <Alert tone="info">{t("stations.licenses.moreLater")}</Alert>
    </div>
  );
}
